import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./help-tooltip.css";

/** Optional context for an already-labelled control. Hover, focus and touch help share one presentation. `content`
 *  is usually a sentence; a compact structured card (a few labelled values) is allowed for summary tiles. */
export function Tooltip({ content, children, openOnClick = false, disabled = false }: {
  content: ReactNode; children: ReactElement<{ "aria-describedby"?: string }>; openOnClick?: boolean; disabled?: boolean;
}) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hovered = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 16, top: 16 });
  // A tip opened by a click stays until dismissed; one opened by hover or keyboard focus follows them.
  const pinned = useRef(false);
  const show = () => { if (disabled) return; clearTimeout(timer.current); setOpen(true); };
  const enter = () => { hovered.current = true; show(); };
  const close = () => { pinned.current = false; setOpen(false); };
  useEffect(() => { if (disabled) { clearTimeout(timer.current); close(); } }, [disabled]);
  // Mouse focus is a by-product of clicking the control: keeping its tip open would cover whatever the pointer goes to
  // next (a QC run clicked the tip instead of the button below it). Only keyboard focus (`:focus-visible`) holds a tip.
  const focusHolds = () => {
    const focused = document.activeElement;
    return Boolean(focused && anchor.current?.contains(focused) && (pinned.current || touched.current || focused.matches(":focus-visible")));
  };
  const leave = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (!hovered.current && !focusHolds()) close(); }, 120);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = (anchor.current?.firstElementChild ?? anchor.current)?.getBoundingClientRect(), box = tip.current?.getBoundingClientRect();
      if (!trigger || !box) return;
      setPosition({ left: Math.max(16, Math.min(trigger.left, innerWidth - box.width - 16)),
        top: Math.max(16, trigger.bottom + box.height + 24 > innerHeight ? trigger.top - box.height - 8 : trigger.bottom + 8) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    // Contextual help is dismissed before its containing inspector or dialog.
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); clearTimeout(timer.current); close(); } };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !anchor.current?.contains(event.target) && !tip.current?.contains(event.target)) close();
    };
    document.addEventListener("keydown", escape, true);
    document.addEventListener("pointerdown", outside);
    return () => { document.removeEventListener("keydown", escape, true); document.removeEventListener("pointerdown", outside); };
  }, [open]);
  // Pressing a control with a mouse hides its hint (it has been read); the pointer re-enters to see it again. A touch
  // has no hover, so its tap still shows the hint through the focus it gives.
  const touched = useRef(false);
  const press = (event: ReactPointerEvent) => {
    touched.current = event.pointerType === "touch";
    if (!openOnClick && !touched.current) { clearTimeout(timer.current); close(); }
  };
  const describedBy = [children.props["aria-describedby"], open && !disabled ? id : undefined].filter(Boolean).join(" ") || undefined;
  return <>
    <span ref={anchor} className="tooltip-anchor" onMouseEnter={enter} onMouseLeave={() => { hovered.current = false; leave(); }}
      onFocusCapture={(event) => { if (touched.current || (event.target as Element).matches(":focus-visible")) show(); }} onBlurCapture={leave}
      onPointerDownCapture={press}
      onClickCapture={openOnClick ? event => { event.preventDefault(); event.stopPropagation(); pinned.current = true; show(); } : undefined}>
      {cloneElement(children, { "aria-describedby": describedBy })}
    </span>
    {open && !disabled && createPortal(<span ref={tip} id={id} role="tooltip" className="help-tooltip" style={position}
      onMouseEnter={enter} onMouseLeave={() => { hovered.current = false; leave(); }}>{content}</span>, anchor.current?.closest("dialog[open],[popover]:popover-open") ?? document.body)}
  </>;
}
